// Phone search layer (Phone-Search board): omnibox, hard letters while focused, results, "➕ Add",
// then the words of the shared base that are not in the dictionary yet.
// Hiding the keyboard with the field empty closes the layer, like "Cancel".
// Adding leaves the layer: a ready card (the word was in the shared base) opens right away; a card
// the worker is still building waits as the first row of the list, so the next word can be added.
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLocale, useTranslations } from 'use-intl';
import { languageName } from '@solungo/core/lang';
import { useCards } from '@/db/hooks';
import { useDictionary } from '@/state/session';
import { useOnline } from '@/state/online';
import { addWord } from '@/state/pending';
import { HardLetters } from '@/components/HardLetters';
import { Close, Search, Spinner } from '@/components/Icons';
import { SearchResults } from '@/components/SearchResults';
import { buildSearchModel } from '@/components/search';
import { useBaseSuggestions } from '@/components/useBaseSuggestions';
import { useCloseOverlay, useEscape } from '@/components/ui';
import { track, type AddSource } from '@/lib/analytics';

export const Route = createFileRoute('/_app/d/$dictionaryId/search')({
  component: SearchScreen,
});

function SearchScreen() {
  const { dictionaryId } = Route.useParams();
  const t = useTranslations('search');
  const tc = useTranslations('common');
  const locale = useLocale();
  const navigate = useNavigate();
  const online = useOnline();
  const dictionary = useDictionary(dictionaryId);
  const cards = useCards(dictionary) ?? [];
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(true);
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const base = useBaseSuggestions(dictionary, query, online);
  const mounted = useRef(true);
  useEffect(() => track('search_opened', { surface: 'phone' }), []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const closeOverlay = useCloseOverlay({ to: '/d/$dictionaryId', params: { dictionaryId } });
  // Several paths can close the layer at once (a tap on "Cancel" blurs the empty field first):
  // one step back in history, not two
  const closed = useRef(false);
  const close = () => {
    if (closed.current) return;
    closed.current = true;
    closeOverlay();
  };
  useEscape(close);
  if (!dictionary) return null;

  // Hiding the keyboard over an empty field closes the layer: there is nothing left to look at.
  // Checked a task later: a blur that only left the window (another app) keeps the field active,
  // and one fired by the field's removal finds it detached.
  const onBlur = () => {
    setFocused(false);
    setTimeout(() => {
      const input = inputRef.current;
      if (!input?.isConnected || input.value !== '') return;
      if (document.activeElement === input || !document.hasFocus()) return;
      close();
    });
  };

  const model = buildSearchModel(query, cards, { targetLang: dictionary.targetLang, baseLang: dictionary.baseLang, online, base });
  const add = (headword: string, source: AddSource = 'search') => {
    if (adding) return;
    setAdding(true);
    void addWord(dictionaryId, headword, source).then((out) => {
      // The user left the layer meanwhile: the list shows the outcome
      if (!mounted.current) return;
      if (out.entryId && out.ready) {
        // The card is ready: show it in place of the search
        void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId: out.entryId }, replace: true });
      } else {
        // Being built by the worker, or failed: the list shows the spinner or the error row
        close();
      }
    });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (adding) return;
    if (model.addHeadword) add(model.addHeadword);
    else if (model.results[0]) {
      track('search_result_opened', { rank: 1, query: model.query, headword: model.results[0].headword });
      void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId: model.results[0].id }, search: { q: model.query } });
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-canvas" data-testid="search-screen">
      <div className="pt-safe flex items-center gap-3 px-4 pt-1.5 box-content">
        <label className="flex h-11 min-w-0 flex-grow items-center gap-2 rounded-field bg-surface px-3 text-ink-secondary shadow-[0_0_0_2px_var(--color-accent)]">
          <Search size={18} />
          <input
            ref={inputRef}
            type="search"
            autoFocus
            value={query}
            placeholder={t('placeholder')}
            aria-label={t('placeholder')}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            readOnly={adding}
            data-testid="omnibox"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={onBlur}
            className="min-w-0 flex-grow bg-transparent text-[17px] text-ink outline-none"
          />
          {adding ? (
            <span className="text-accent" data-testid="omnibox-adding">
              <Spinner />
            </span>
          ) : query ? (
            <button type="button" aria-label={t('clear')} className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-quaternary text-ink-inverse" onMouseDown={(e) => e.preventDefault()} onClick={() => setQuery('')}>
              <Close size={12} />
            </button>
          ) : null}
        </label>
        <button type="button" className="h-11 flex-shrink-0 whitespace-nowrap text-[17px] text-accent" onClick={close}>
          {tc('cancel')}
        </button>
      </div>
      {focused && (
        <div className="px-4 pt-3">
          <HardLetters lang={dictionary.targetLang} target={inputRef} />
        </div>
      )}
      <div className="flex flex-grow flex-col gap-2.5 overflow-y-auto px-4 pb-8 pt-4">
        {adding ? null : model.query ? (
          <>
            <SearchResults model={model} dictionaryId={dictionaryId} targetLang={dictionary.targetLang} baseLang={dictionary.baseLang} onAdd={add} />
            {!online && model.results.length === 0 && <div className="px-1 text-[13px] text-ink-secondary">{t('offlineAdd')}</div>}
          </>
        ) : (
          <div className="px-1 pt-2 text-[15px] leading-5 text-ink-secondary">
            {t('typeToSearch', { target: languageName(dictionary.targetLang, locale), base: languageName(dictionary.baseLang, locale) })}
          </div>
        )}
      </div>
    </div>
  );
}
